import { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  File,
  FileText,
  Paperclip,
  Send,
  Sparkles,
  X
} from "lucide-react";
import type { EmailAccount, EmailContact, EmailTemplate } from "../types";
import { api } from "../lib/api";
import { fileSize } from "../lib/format";
import RichTextEditor from "./RichTextEditor";
import { Badge, Button, IconButton, Modal } from "./ui";

export interface ComposerSeed {
  to?: string;
  subject?: string;
  bodyHtml?: string;
  accountId?: string;
  templateId?: string;
  cc?: string;
  bcc?: string;
  draftId?: string;
  relatedMessageId?: string;
  action?: "reply" | "replyall" | "forward";
}

const SIGNATURE_PATTERN = /<div class=["']unify-signature["'][^>]*>[\s\S]*?<\/div>\s*$/i;

function applySignature(bodyHtml: string, account?: EmailAccount) {
  const body = bodyHtml.replace(SIGNATURE_PATTERN, "").trim();
  if (!account?.signatureHtml.trim()) return body;
  return `${body}<div class="unify-signature"><br>${account.signatureHtml}</div>`;
}

export default function Composer({
  open,
  accounts,
  templates,
  seed,
  onClose,
  onSent,
  notify
}: {
  open: boolean;
  accounts: EmailAccount[];
  templates: EmailTemplate[];
  seed?: ComposerSeed;
  onClose: () => void;
  onSent: () => void;
  notify: (tone: "success" | "error" | "info", title: string, message?: string) => void;
}) {
  const sendable = useMemo(
    () => accounts.filter((account) => account.isActive && account.permissions.canSend),
    [accounts]
  );
  const [accountId, setAccountId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [showCopies, setShowCopies] = useState(false);
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [draftId, setDraftId] = useState<string>();
  const [contacts, setContacts] = useState<EmailContact[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const selectedTemplate = useMemo(() => templates.find((item) => item.id === templateId), [templates, templateId]);

  useEffect(() => {
    if (!open) return;
    const initialAccountId = seed?.accountId ?? sendable[0]?.id ?? "";
    setAccountId(initialAccountId);
    setTo(seed?.to ?? "");
    setSubject(seed?.subject ?? "");
    setBodyHtml(applySignature(
      seed?.bodyHtml ?? "",
      sendable.find((account) => account.id === initialAccountId)
    ));
    setTemplateId(seed?.templateId ?? "");
    setCc(seed?.cc ?? "");
    setBcc(seed?.bcc ?? "");
    setDraftId(seed?.draftId);
    setFiles([]);
    setShowCopies(false);
    api.contacts().then(setContacts).catch(() => setContacts([]));
  }, [open, seed, sendable]);

  const draftPayload = () => ({
    accountId,
    to,
    cc,
    bcc,
    subject,
    bodyHtml,
    templateId: templateId || null
  });

  const saveDraft = async () => {
    if (!accountId || (!to.trim() && !subject.trim() && !bodyHtml.trim())) return draftId;
    const saved = draftId
      ? await api.updateDraft(draftId, draftPayload())
      : await api.createDraft(draftPayload());
    setDraftId(saved.id);
    return saved.id;
  };

  useEffect(() => {
    if (!open || sending || !accountId || (!to.trim() && !subject.trim() && !bodyHtml.trim())) return;
    const timer = window.setTimeout(() => { saveDraft().catch(() => undefined); }, 1500);
    return () => window.clearTimeout(timer);
  }, [open, sending, accountId, to, cc, bcc, subject, bodyHtml, templateId]);

  const saveAndClose = async () => {
    try {
      await saveDraft();
      if (to.trim() || subject.trim() || bodyHtml.trim()) notify("info", "Draft saved");
    } catch (error) {
      notify("error", "Couldn’t save draft", (error as Error).message);
    }
    onClose();
  };

  const applyTemplate = (id: string) => {
    setTemplateId(id);
    const template = templates.find((item) => item.id === id);
    if (!template) return;
    setSubject(template.subjectTemplate);
    setBodyHtml(applySignature(
      template.bodyHtmlTemplate,
      sendable.find((account) => account.id === accountId)
    ));
  };

  const changeAccount = (nextAccountId: string) => {
    setAccountId(nextAccountId);
    setBodyHtml((current) =>
      applySignature(current, sendable.find((account) => account.id === nextAccountId))
    );
  };

  const attachFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    setFiles((current) => [...current, ...Array.from(incoming)].slice(0, 10));
  };

  const submit = async () => {
    if (!accountId || !to.trim() || !subject.trim()) {
      notify("error", "A few details are missing", "Choose an account, recipient, and subject.");
      return;
    }
    setSending(true);
    const form = new FormData();
    form.set("accountId", accountId);
    form.set("templateId", templateId);
    form.set("to", to);
    form.set("cc", cc);
    form.set("bcc", bcc);
    form.set("subject", subject);
    form.set("bodyHtml", bodyHtml);
    if (draftId) form.set("draftId", draftId);
    if (seed?.relatedMessageId) form.set("relatedMessageId", seed.relatedMessageId);
    if (seed?.action) form.set("action", seed.action);
    files.forEach((file) => form.append("attachments", file));
    try {
      const result = await api.sendMessage(form);
      notify("success", "Message ready", result.message);
      onSent();
      onClose();
    } catch (error) {
      notify("error", "Couldn’t send message", (error as Error).message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={saveAndClose}
      title="New message"
      subtitle="Everything is sent securely through the connected server."
      wide
    >
      <div className="composer-layout">
        <div className="composer-fields">
          <div className="compose-row">
            <label>From</label>
            <div className="select-wrap compose-control">
              <select value={accountId} onChange={(event) => changeAccount(event.target.value)}>
                {sendable.map((account) => (
                  <option value={account.id} key={account.id}>
                    {account.displayName} · {account.emailAddress}
                  </option>
                ))}
              </select>
              <ChevronDown size={15} />
            </div>
          </div>
          <div className="compose-row">
            <label>Template</label>
            <div className="select-wrap compose-control">
              <select value={templateId} onChange={(event) => applyTemplate(event.target.value)}>
                <option value="">Start from a blank message</option>
                {templates
                  .filter((template) => template.isActive)
                  .map((template) => (
                    <option value={template.id} key={template.id}>
                      {template.category} · {template.name}
                    </option>
                  ))}
              </select>
              <ChevronDown size={15} />
            </div>
            {templateId && <Badge tone="violet">Editable copy</Badge>}
          </div>
          <div className="compose-row">
            <label htmlFor="compose-to">To</label>
            <input
              id="compose-to"
              className="compose-control"
              value={to}
              placeholder="recipient@example.com"
              onChange={(event) => setTo(event.target.value)}
              list="compose-contacts"
            />
            <button type="button" className="copy-toggle" onClick={() => setShowCopies(!showCopies)}>
              Cc / Bcc
            </button>
          </div>
          {showCopies && (
            <>
              <div className="compose-row">
                <label htmlFor="compose-cc">Cc</label>
                <input
                  id="compose-cc"
                  className="compose-control"
                  value={cc}
                  onChange={(event) => setCc(event.target.value)}
                  list="compose-contacts"
                />
              </div>
              <div className="compose-row">
                <label htmlFor="compose-bcc">Bcc</label>
                <input
                  id="compose-bcc"
                  className="compose-control"
                  value={bcc}
                  onChange={(event) => setBcc(event.target.value)}
                  list="compose-contacts"
                />
              </div>
            </>
          )}
          <div className="compose-row">
            <label htmlFor="compose-subject">Subject</label>
            <input
              id="compose-subject"
              className="compose-control compose-subject"
              value={subject}
              placeholder="What’s this message about?"
              onChange={(event) => setSubject(event.target.value)}
            />
          </div>
          <datalist id="compose-contacts">
            {contacts.map((contact) => (
              <option
                key={contact.emailAddress}
                value={contact.name
                  ? `${contact.name} <${contact.emailAddress}>`
                  : contact.emailAddress}
              />
            ))}
          </datalist>
        </div>

        {templateId && (
          <div className="template-applied-note">
            <Sparkles size={17} />
            <span>
              Template loaded. You can freely change its subject, wording, formatting, and text color
              before sending.
              {(selectedTemplate?.defaultAttachments?.length ?? 0) > 0 &&
                ` ${selectedTemplate!.defaultAttachments!.length} default attachment(s) will be included.`}
            </span>
          </div>
        )}
        <RichTextEditor value={bodyHtml} onChange={setBodyHtml} minHeight={260} />

        {files.length > 0 && (
          <div className="attachment-strip">
            {files.map((file, index) => (
              <div className="attachment-chip" key={`${file.name}-${index}`}>
                <span className="attachment-chip-icon">
                  {file.type.startsWith("text/") ? <FileText size={17} /> : <File size={17} />}
                </span>
                <span>
                  <strong>{file.name}</strong>
                  <small>{fileSize(file.size)}</small>
                </span>
                <IconButton
                  label={`Remove ${file.name}`}
                  onClick={() => setFiles(files.filter((_, itemIndex) => itemIndex !== index))}
                >
                  <X size={15} />
                </IconButton>
              </div>
            ))}
          </div>
        )}

        <div className="composer-footer">
          <div>
            <input
              ref={fileInput}
              type="file"
              multiple
              hidden
              onChange={(event) => attachFiles(event.target.files)}
            />
            <Button
              tone="ghost"
              icon={<Paperclip size={17} />}
              onClick={() => fileInput.current?.click()}
            >
              Attach files
            </Button>
            <span className="attachment-limit">Up to 25 MB total</span>
          </div>
          <div className="footer-actions">
            <Button tone="secondary" onClick={saveAndClose}>
              Save & close
            </Button>
            <Button
              icon={<Send size={17} />}
              onClick={submit}
              disabled={sending || sendable.length === 0}
            >
              {sending ? "Sending…" : "Send message"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}


